import { useIncludeCashFilterFunc } from '@src/hooks/useIncludeCashFilter';
import { filterExistsAndExcludes, useSecuritiesContext } from '@talos/kyoko';
import { useCallback } from 'react';
import { getPositionsBlotterRowAsset } from '../positionBlotterRow';
import type { PositionsTableFilter } from '../types';
import { usePositionsExpiryFilter } from '../usePositionsExpiryFilter';
import type { UnifiedPosition } from './UnifiedPosition';
import { isUnifiedPositionExcludedByCurrencyOrBasePairFilters } from './unifiedCurrencyBasePairFilter';

interface UseUnifiedPositionsClientFilterParams {
  filter: PositionsTableFilter;
  includeCash: boolean;
}

/**
 * Client-side rxjs filter shared by desktop and mobile Unified positions blotters.
 * Composes expiry, market-account-group, product, currency/base-pair, and include-cash filtering.
 */
export function useUnifiedPositionsClientFilter({
  filter,
  includeCash,
}: UseUnifiedPositionsClientFilterParams): (position: UnifiedPosition) => boolean {
  const includeCashFilterFunc = useIncludeCashFilterFunc({
    includeCash,
    assetGetter: getPositionsBlotterRowAsset,
  });
  const expiryFilter = usePositionsExpiryFilter(getPositionsBlotterRowAsset, filter.Expiries);
  const { securitiesBySymbol } = useSecuritiesContext();

  return useCallback(
    (position: UnifiedPosition) => {
      if (!expiryFilter(position)) {
        return false;
      }
      if (filterExistsAndExcludes(filter, 'MarketAccountGroups', position, 'marketAccountGroup')) {
        return false;
      }
      if (filterExistsAndExcludes(filter, 'AssetTypes', position, 'AssetType')) {
        return false;
      }
      if (
        isUnifiedPositionExcludedByCurrencyOrBasePairFilters(
          position.Asset,
          securitiesBySymbol,
          filter.Currencies,
          filter.BasePairs
        )
      ) {
        return false;
      }
      if (!includeCashFilterFunc(position)) {
        return false;
      }
      return true;
    },
    [expiryFilter, filter, includeCashFilterFunc, securitiesBySymbol]
  );
}
